import { Controller, useForm, useFormContext } from 'react-hook-form';
import { DatePicker, Input, UISelect } from '../common/forms';
import React, { useEffect, useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { successMessage } from '~/utils/toasts';

import Axios from '~/utils/axios';
import { DateTime } from 'luxon';
import { RequestFooter } from './request-footer';
import { sentryCaptureException } from '~/errors/sentry';
import { useRouter } from 'next/router';
import { renderErrorsToToaster } from './render-error-to-toaster';
import { AddButton } from '../common/button/add';
import { TrashIconOutline } from '~/svgs';
import classNames from 'classnames';

export const AdditionalServices = ({ nationalCarriersList }: any) => {
   const { lang, translate } = useTranslate();
   const router = useRouter();
   const [loading, setLoading] = useState(false);
   const { id } = router.query;
   const { getValues, reset } = useFormContext();
   const tripServices = getValues('tripServices');
   const [services, setServices] = useState(tripServices ? tripServices.length - 1 : 0);

   const servicesList = [
      {
         value: 'FULL_TRANSPORTATION_JOURNEY',
         label: translate({ id: 'visasManager:full transportation journey' }),
      },
      {
         value: 'MAZARAT',
         label: translate({ id: 'visasManager:mazarat' }),
      },
      {
         value: 'TOURING_SERVICES',
         label: translate({ id: 'visasManager:touring services' }),
      },
      {
         value: 'HEALTH_SERVICES',
         label: translate({ id: 'visasManager:health services' }),
      },
      {
         value: 'OTHERS',
         label: translate({ id: 'visasManager:others' }),
      },
   ];

   const {
      register,
      control,
      formState: { errors },
      setValue,
      handleSubmit,
      watch,
   } = useForm({
      defaultValues: {
         accommodations: tripServices ? [...tripServices] : undefined,
      } as any,
   });
   const startDate = watch('startDate');
   const endDate = watch('endDate');
   useEffect(() => {
      if (endDate && !startDate) {
         const endDateAsDate = DateTime.fromFormat(endDate, 'yyyy-MM-dd').toJSDate();
         const minStartDate = DateTime.fromJSDate(endDateAsDate).plus({ days: 1 });
         setValue('startDate', minStartDate.toFormat('yyyy-MM-dd'));
      }
   }, [endDate, startDate, setValue]);

   const submitForm = async (values: any) => {
      console.log(values);

      const servicesArray = (
         !Array.isArray(values?.tripServices) ? Object.values(values?.tripServices) : []
      )?.map?.((service: any) => ({
         ...service,
         servicePrice: parseFloat(service?.servicePrice || 0)?.toFixed(2),
      }));

      const cleanValues = Array.isArray(values?.tripServices)
         ? values?.tripServices
              ?.filter?.((service: null) => service !== null)
              ?.map((item: any) => ({
                 ...item,
                 servicePrice: parseFloat(item?.servicePrice || 0)?.toFixed(2),
              }))
         : { tripServices: servicesArray };
      const newValues = {
         current_step: 'main_information_additional_services',
         ...cleanValues,
      };
      setLoading(true);
      try {
         const { data } = await Axios.patch(`/dhamen/v1/visa-request/${id}`, newValues);
         if (data?.status.toLowerCase() === 'success') {
            await Axios.patch(`/dhamen/v1/visa-request/${id}`, {
               current_step: 'main_information_done',
            });
            reset({ ...getValues(), ...values });
            successMessage(translate({ id: 'visasManager:request created successfully' }));
            router.push(`/${lang}/umrah-requests`);
         } else throw new Error(data?.error || data?.original?.error || 'Something went wrong');
      } catch (error: any) {
         console.log(error);
         sentryCaptureException(error);
         renderErrorsToToaster(error?.response?.data);
      }
      setLoading(false);
   };

   const Service = ({ index }: { index: number }) => {
      const isRequired =
         watch(`tripServices[${index}].serviceType`) ||
         (Boolean(getValues('entryType') === 'AIR' || getValues('entryType') === 'SEA') &&
            index == 0);

      if (isRequired && !watch(`tripServices[${index}].serviceType`)) {
         setValue(`tripServices[${index}].serviceType`, servicesList?.[0]?.value || '');
      }

      const handleDelete = () => {
         setServices(prevServices => Math.max(prevServices - 1, 0));
         setValue(`tripServices[${index}]`, null);
      };

      const validateServicePrice = (value: any) => {
         if (!isRequired && !value) return true;
         if (value <= 0) {
            return translate({ id: 'validation:required' });
         }

         if (value?.startsWith?.('0')) {
            return translate({ id: 'validation:not starting with zero' });
         }

         const validNumberRegex = /^[0-9٠-٩]+(\.[0-9٠-٩]{1,2})?$/;

         if (!validNumberRegex.test(value)) {
            return translate({ id: 'validation:invalid format hotel price' });
         }

         return true;
      };

      return (
         <div className="p-5 mb-8 border rounded-md bg-grey-600 border-grey-500 last:mb-0">
            <div className="mb-5">
               <Controller
                  name={`tripServices[${index}].serviceType`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                     <UISelect
                        placeholder={translate({
                           id: 'visasManager:service type',
                        })}
                        label={translate({
                           id: 'visasManager:select a service',
                        })}
                        id="serviceType"
                        value={servicesList.find((opt: any) => opt.value === value) || value}
                        // isDisabled={Boolean(isRequired && servicesList?.[0])}
                        className={classNames(
                           isRequired && getValues('entryType') !== 'LAND' && servicesList?.[0]
                              ? 'pointer-events-none'
                              : null
                        )}
                        options={servicesList}
                        error={(errors as any)?.tripServices?.[index]?.serviceType?.message}
                        onChange={({ value }: any) => onChange(value)}
                     />
                  )}
               />
            </div>
            <div className="mb-5">
               <Input
                  id={`tripServices[${index}].serviceProvider`}
                  label={<Translate id="visasManager:service provider" />}
                  placeholder={translate({
                     id: 'visasManager:enter service provider name',
                  })}
                  error={(errors as any)?.tripServices?.[index]?.serviceProvider?.message}
                  {...register(`tripServices[${index}].serviceProvider`, {
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                     maxLength: {
                        value: 200,
                        message: translate({
                           id: 'validation:max characters',
                           values: {
                              count: String(200),
                           },
                        }),
                     },
                  })}
                  isRequired={isRequired}
               />
            </div>
            <div className="mb-5">
               <Controller
                  name={`tripServices[${index}].transporter`}
                  control={control}
                  rules={{
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                  }}
                  render={({ field: { onChange, value } }) => (
                     <UISelect
                        placeholder={translate({
                           id: 'visasManager:transporter',
                        })}
                        label={translate({
                           id: 'visasManager:select a transporter',
                        })}
                        id="transporter"
                        value={nationalCarriersList.find((opt: any) => opt.value === value)}
                        options={nationalCarriersList}
                        error={(errors as any)?.tripServices?.[index]?.transporter?.message}
                        onChange={({ value }: any) => onChange(value)}
                        isRequired={isRequired}
                     />
                  )}
               />
            </div>
            <div className="mb-5">
               <Input
                  id={`tripServices[${index}].transportationDetails`}
                  label={<Translate id="visasManager:transportaion details" />}
                  placeholder={translate({
                     id: 'visasManager:enter transportaion details',
                  })}
                  error={(errors as any)?.tripServices?.[index]?.transportationDetails?.message}
                  {...register(`tripServices[${index}].transportationDetails`, {
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                     maxLength: {
                        value: 100,
                        message: translate({
                           id: 'validation:max characters',
                           values: {
                              count: String(100),
                           },
                        }),
                     },
                  })}
                  type="textarea"
                  rows={4}
                  isRequired={isRequired}
               />
            </div>
            <div className="flex flex-col items-start justify-between gap-5 mb-5 lg:flex-row">
               <Controller
                  control={control}
                  name={`tripServices[${index}].startDate`}
                  rules={{
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                  }}
                  render={({ field: { onChange, value } }) => (
                     <DatePicker
                        placeholderText={translate({
                           id: 'common:date',
                        })}
                        id="startDate"
                        minDate={
                           DateTime.fromFormat(
                              getValues('dateArrivalFlight'),
                              'yyyy-MM-dd'
                           ).toJSDate() ?? new Date()
                        }
                        maxDate={
                           DateTime.fromFormat(
                              getValues('flightDepartureDate'),
                              'yyyy-MM-dd'
                           ).toJSDate() ?? null
                        }
                        label={translate({ id: 'visasManager:start date' })}
                        value={value?.split?.('T')?.[0] || value}
                        error={(errors as any)?.tripServices?.[index]?.startDate?.message}
                        onChange={val => {
                           const d = DateTime.fromJSDate(val as Date);
                           onChange(d.toFormat('yyyy-MM-dd'));
                           setValue(`tripServices[${index}].endDate`, ''); // Reset end date when start date changes
                        }}
                        groupClassName="w-full lg:w-1/2"
                        isRequired={isRequired}
                     />
                  )}
               />
               <Controller
                  control={control}
                  name={`tripServices[${index}].endDate`}
                  rules={{
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                  }}
                  render={({ field: { onChange, value } }) => {
                     const startDateValue = watch(`tripServices[${index}].startDate`);
                     const minDate =
                        (startDateValue &&
                           DateTime.fromFormat(startDateValue, 'yyyy-MM-dd').toJSDate()) ||
                        DateTime.fromFormat(
                           getValues('dateArrivalFlight'),
                           'yyyy-MM-dd'
                        ).toJSDate() ||
                        new Date();

                     return (
                        <DatePicker
                           placeholderText={translate({
                              id: 'common:date',
                           })}
                           id="endDate"
                           minDate={minDate}
                           maxDate={
                              DateTime.fromFormat(
                                 getValues('flightDepartureDate'),
                                 'yyyy-MM-dd'
                              ).toJSDate() ?? null
                           }
                           label={translate({ id: 'visasManager:end date' })}
                           value={value?.split?.('T')?.[0] || value}
                           error={(errors as any)?.tripServices?.[index]?.endDate?.message}
                           onChange={val => {
                              const d = DateTime.fromJSDate(val as Date);
                              onChange(d.toFormat('yyyy-MM-dd'));
                           }}
                           groupClassName="w-full lg:w-1/2"
                           isRequired={isRequired}
                        />
                     );
                  }}
               />
            </div>
            <div className="mb-5">
               <Input
                  id={`tripServices[${index}].serviceDetails`}
                  label={<Translate id="visasManager:service details" />}
                  placeholder={translate({ id: 'visasManager:enter service details' })}
                  error={(errors as any)?.tripServices?.[index]?.serviceDetails?.message}
                  {...register(`tripServices[${index}].serviceDetails`, {
                     required: isRequired ? translate({ id: 'validation:required' }) : false,

                     maxLength: {
                        value: 200,
                        message: translate({
                           id: 'validation:max characters',
                           values: {
                              count: String(200),
                           },
                        }),
                     },
                  })}
                  type="textarea"
                  rows={4}
                  isRequired={isRequired}
               />
            </div>
            <div className="mb-5">
               <Input
                  id={`tripServices[${index}].servicePrice`}
                  label={translate({
                     id: 'visasManager:service price',
                  })}
                  placeholder={translate({
                     id: 'visasManager:enter service price',
                  })}
                  isRequired={isRequired}
                  error={(errors as any)?.tripServices?.[index]?.servicePrice?.message}
                  {...register(`tripServices[${index}].servicePrice`, {
                     required: isRequired ? translate({ id: 'validation:required' }) : false,
                     validate: validateServicePrice,
                  })}
                  description={<Translate id="visasManager:service price note" />}
               />
            </div>
            {services > 0 && (
               <div className="mt-5">
                  <button
                     type="button"
                     onClick={handleDelete}
                     className="flex items-center gap-2 text-states-error hover:text-red-700 focus:outline-none">
                     <TrashIconOutline />
                     <Translate id="common:delete" />
                  </button>
               </div>
            )}
         </div>
      );
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:additional services" />
            </div>

            <div className="w-full pb-8">
               <form onSubmit={handleSubmit(submitForm)}>
                  <Service index={0} />
                  {[...Array(services)].map((_, i) => (
                     <Service key={i + 1} index={i + 1} />
                  ))}
               </form>
            </div>
            <div className="my-5">
               <div className="my-5">
                  <AddButton
                     onClick={() => {
                        setServices(prevServices => prevServices + 1);
                        setValue(`tripServices[${services + 1}]`, {}); // Use updated value of services
                     }}
                     title={<Translate id="visasManager:add another service" />}
                  />
               </div>
            </div>
         </div>
         <RequestFooter
            fwdBtnHandler={handleSubmit(submitForm)}
            fwdBtnLabel={translate({ id: 'visasManager:create request' })}
            cancelBtnHandler={() => router.back()}
            loading={loading}
         />
      </>
   );
};
